// Сайдбар пульта (задача D5, ui-spec §2, референс AirOps): белая панель `w-56` на подложке.
//
// Сверху вниз: знак «✦ Анна» (Cuprum) и «ChinaToday»; пилюля статуса; «Поиск» с подсказкой «Ctrl K»; разделы роли
// (`h-8`, иконка 16 px цветом --muted-foreground; активный — фон --muted и `aria-current="page"`; счётчик справа
// подключит D18); внизу — пользователь и его меню.
//
// Вид `compact` (1024–1279) — только иконки, названия — в тултипах справа. Уже 1024 сайдбара нет: то же содержимое
// открывается из «☰» в верхней полосе (`SidebarContent` в выезжающей панели).
import { useMe } from '@/api/queries/auth';
import { Search } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { isActiveHref, navItems, type NavItem } from './nav';
import { StatusPill } from './status-pill';
import { UserMenu } from './user-menu';

function Brand({ compact }: { readonly compact: boolean }) {
  if (compact) {
    return (
      <span className="mx-auto pb-2 font-mark text-md font-bold" title="Анна · ChinaToday">
        <span aria-hidden="true">✦</span>
        <span className="sr-only">Анна, ChinaToday</span>
      </span>
    );
  }
  return (
    <div className="flex items-baseline gap-2 px-2 pt-0.5 pb-2.5">
      <span className="font-mark text-md font-bold">
        <span aria-hidden="true">✦ </span>Анна
      </span>
      <span className="text-sm text-muted-foreground">ChinaToday</span>
    </div>
  );
}

function SearchButton({ compact, onOpen }: { readonly compact: boolean; readonly onOpen: () => void }) {
  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger
          aria-label="Поиск и команды"
          onClick={onOpen}
          className="btn mx-auto mb-1.5 flex size-8 cursor-pointer items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-surface-hover"
        >
          <Search aria-hidden="true" className="size-4" />
        </TooltipTrigger>
        <TooltipContent side="right">Поиск · Ctrl K</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="btn mb-1.5 flex h-8 w-full cursor-pointer items-center gap-2 rounded-md border border-input px-2 text-muted-foreground hover:bg-surface-hover max-sm:h-10"
    >
      <Search aria-hidden="true" className="size-4" />
      <span>Поиск</span>
      <kbd className="ml-auto inline-flex h-5 items-center rounded-sm border border-border-strong px-1 font-sans text-sm text-muted-foreground max-sm:hidden">
        Ctrl K
      </kbd>
    </button>
  );
}

function NavLink({ item, active, compact, onNavigate }: { readonly item: NavItem; readonly active: boolean; readonly compact: boolean; readonly onNavigate?: () => void }) {
  const className = cn(
    'flex h-8 items-center gap-2 rounded-md text-base text-foreground hover:bg-surface-hover max-sm:h-10',
    compact ? 'mx-auto w-8 justify-center' : 'px-2',
    active && 'bg-muted font-medium hover:bg-muted',
  );
  const icon = <item.icon aria-hidden="true" className={cn('size-4 shrink-0', active ? 'text-foreground' : 'text-muted-foreground')} />;
  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={<Link to={item.href} aria-label={item.titleRu} aria-current={active ? 'page' : undefined} className={className} onClick={onNavigate} />}
        >
          {icon}
        </TooltipTrigger>
        <TooltipContent side="right">{item.titleRu}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Link to={item.href} aria-current={active ? 'page' : undefined} className={className} onClick={onNavigate}>
      {icon}
      <span className="min-w-0 truncate">{item.titleRu}</span>
    </Link>
  );
}

export interface SidebarContentProps {
  readonly compact?: boolean;
  readonly onOpenPalette: () => void;
  /** «Клавиши» в меню пользователя — шпаргалка «?». */
  readonly onOpenShortcuts: () => void;
  /** Пункт меню нажат (меню телефона закрывается). */
  readonly onNavigate?: () => void;
  /** Главный экземпляр объявляет перемены статуса; копия в меню телефона — нет. */
  readonly announce?: boolean;
}

/** Содержимое сайдбара — и в сайдбаре, и в выезжающем меню телефона. */
export function SidebarContent({ compact = false, onOpenPalette, onOpenShortcuts, onNavigate, announce = false }: SidebarContentProps) {
  const me = useMe();
  const { pathname } = useLocation();
  if (me === undefined) return null;
  const items = navItems(me.user.role);
  return (
    <div className="flex h-full min-h-0 flex-col gap-0.5">
      <Brand compact={compact} />
      <div className="mb-1.5">
        <StatusPill variant={compact ? 'compact' : 'sidebar'} announce={announce} />
      </div>
      <SearchButton compact={compact} onOpen={onOpenPalette} />
      <nav aria-label="Разделы пульта" className="min-h-0 flex-1 overflow-y-auto">
        <ul className="flex flex-col gap-0.5">
          {items.map((item) => (
            <li key={item.id}>
              <NavLink
                item={item}
                active={isActiveHref(item.href, pathname)}
                compact={compact}
                {...(onNavigate === undefined ? {} : { onNavigate })}
              />
            </li>
          ))}
        </ul>
      </nav>
      <div className="mt-auto border-t border-border pt-2.5">
        <UserMenu compact={compact} onOpenShortcuts={onOpenShortcuts} />
      </div>
    </div>
  );
}

export function Sidebar({
  compact,
  onOpenPalette,
  onOpenShortcuts,
}: {
  readonly compact: boolean;
  readonly onOpenPalette: () => void;
  readonly onOpenShortcuts: () => void;
}) {
  return (
    <aside
      aria-label="Сайдбар пульта"
      className={cn(
        'sticky top-2 flex h-[calc(100dvh-1rem)] shrink-0 flex-col rounded-xl border border-border bg-card py-3',
        // Иконками — 80 px: под точкой состояния помещается слово («Не работает»), а не только цвет.
        compact ? 'w-20 px-1.5' : 'w-56 px-2',
      )}
    >
      <SidebarContent compact={compact} onOpenPalette={onOpenPalette} onOpenShortcuts={onOpenShortcuts} announce />
    </aside>
  );
}
